/**
 * InspectionReviewPanel — Embedded inside the existing Job Detail dialog in
 * RequestMonitoring.js. Shows the manager all 15 steps' data + manager review actions.
 *
 * Pure additive component. Renders nothing if no inspection workflow exists.
 */
import { useEffect, useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Check, AlertCircle, Loader2, FileDown, ShieldAlert, Droplet, Camera,
  ClipboardCheck, ThumbsUp, MessageCircle, X, Award
} from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const STATUS_BADGE = {
  complete:    "bg-emerald-100 text-emerald-700",
  in_progress: "bg-blue-100 text-blue-700",
  na:          "bg-zinc-200 text-zinc-600",
  incomplete:  "bg-rose-100 text-rose-700",
};
const STEP_NAMES = [
  null, "Arrival & PIN", "Customer Info", "Inspection Consent", "Damage Type",
  "Damage Photos", "Scope of Work", "Moisture Assessment", "Floor Plan",
  "Equipment Placement", "Hazards", "Inventory", "Customer Debrief",
  "Estimate Summary", "Paperwork & Signature", "Sign-off",
];

export default function InspectionReviewPanel({ requestId, onAfterAction }) {
  const [data, setData] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState(null);  // "changes" | "reject" | "certificate"
  const [actionNotes, setActionNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [insp, ph] = await Promise.all([
          API.get(`/inspection/${requestId}`).catch(() => null),
          API.get(`/photos/job/${requestId}`).catch(() => ({ data: { photos: [] } })),
        ]);
        if (cancelled) return;
        setData(insp?.data || null);
        setPhotos(Array.isArray(ph?.data?.photos) ? ph.data.photos : []);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [requestId]);

  if (loading) return (
    <div className="py-6 text-center text-zinc-400"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
  );
  if (!data || !data.workflow?.submitted_at) {
    return (
      <div className="text-xs text-zinc-500 italic py-4 text-center" data-testid="inspection-not-submitted">
        No completed inspection has been submitted for this job yet.
      </div>
    );
  }

  const { workflow: wf, request } = data;
  const s2  = wf.step_2  || {}; const s4  = wf.step_4  || {};
  const s6  = wf.step_6?.ai_report || {}; const s7 = wf.step_7 || {};
  const s9  = wf.step_9  || {}; const s10 = wf.step_10 || {};
  const s11 = wf.step_11 || {}; const s12 = wf.step_12 || {};
  const s13 = wf.step_13 || {}; const s14 = wf.step_14 || {};
  const isPending = request.estimate_status === "Estimate Pending Review";
  const reviewedAt = request.estimate_approved_at || request.manager_review_at;

  const callAction = async (path, payload) => {
    setBusy(true);
    try {
      const { data: out } = await API.post(`/inspection/${requestId}/manager/${path}`, payload || undefined);
      toast.success(`Status → ${out.estimate_status || out.certificate_url ? "Updated" : ""}`);
      setActionDialog(null); setActionNotes("");
      if (onAfterAction) onAfterAction();
      // refresh
      const insp = await API.get(`/inspection/${requestId}`);
      setData(insp.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 text-xs" data-testid="inspection-review-panel">

      {/* Pending Review Banner with action buttons */}
      {isPending && (
        <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3" data-testid="estimate-pending-banner">
          <div className="flex items-start gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-zinc-900">Estimate Pending Review</p>
              <p className="text-[11px] text-zinc-600">
                Submitted by Technician on {new Date(wf.submitted_at).toLocaleString()}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            <Button onClick={() => callAction("approve")} disabled={busy}
                    className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px]"
                    data-testid="mgr-approve-btn">
              <ThumbsUp className="w-3 h-3 mr-1" /> Approve
            </Button>
            <Button onClick={() => setActionDialog("changes")} disabled={busy} variant="outline"
                    className="h-9 text-[11px]"
                    data-testid="mgr-request-changes-btn">
              <MessageCircle className="w-3 h-3 mr-1" /> Changes
            </Button>
            <Button onClick={() => setActionDialog("reject")} disabled={busy} variant="outline"
                    className="h-9 border-rose-300 text-rose-700 text-[11px]"
                    data-testid="mgr-reject-btn">
              <X className="w-3 h-3 mr-1" /> Reject
            </Button>
          </div>
        </div>
      )}

      {/* Status badges */}
      {!isPending && request.estimate_status && (
        <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3" data-testid="estimate-status-banner">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Estimate Status</p>
          <p className="text-sm font-bold text-zinc-900 mt-0.5">{request.estimate_status}</p>
          {reviewedAt && <p className="text-[10px] text-zinc-500">Reviewed {new Date(reviewedAt).toLocaleString()}</p>}
          {request.manager_review_notes && (
            <p className="text-[11px] text-zinc-700 mt-1 italic">"{request.manager_review_notes}"</p>
          )}
          {request.estimate_status === "Approved" && !request.certificate_url && (
            <Button size="sm" onClick={() => setActionDialog("certificate")} disabled={busy}
                    className="mt-2 h-9 bg-purple-600 hover:bg-purple-700 text-white text-[11px]"
                    data-testid="mgr-issue-cert-btn">
              <Award className="w-3 h-3 mr-1" /> Mark Fully Complete + Issue Certificate
            </Button>
          )}
          {request.certificate_url && (
            <Button size="sm" variant="outline" className="mt-2 text-[11px] h-8"
                    onClick={() => window.open(`${API_BASE}${request.certificate_url}`, "_blank")}
                    data-testid="mgr-view-cert">
              <FileDown className="w-3 h-3 mr-1" /> View Certificate of Satisfaction
            </Button>
          )}
        </div>
      )}

      {/* 15-step status table */}
      <Section title="15-Step Status">
        <div className="grid grid-cols-1 gap-1">
          {Array.from({ length: 15 }).map((_, i) => {
            const n = i + 1;
            const st = (wf.steps?.[String(n)] || {}).status || "incomplete";
            return (
              <div key={n} className="flex items-center justify-between" data-testid={`ir-step-${n}`}>
                <span className="text-zinc-700 text-[11px]">{n}. {STEP_NAMES[n]}</span>
                <Badge className={`text-[9px] ${STATUS_BADGE[st]}`}>{st}</Badge>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Customer + Damage profile */}
      <Section title="Customer & Damage">
        <KV label="Customer" v={s2.full_name || request.customer_name} />
        <KV label="Payment" v={s2.payment_type} />
        {s2.payment_type === "Insurance" && <>
          <KV label="Insurance Co." v={s2.insurance_company} />
          <KV label="Claim #" v={s2.claim_number} />
          <KV label="Adjuster" v={s2.adjuster_name} />
        </>}
        <KV label="Damage Type" v={s4.primary_damage_type + (s4.primary_damage_type_other ? ` — ${s4.primary_damage_type_other}` : "")} />
        <KV label="Affected Areas" v={(s4.affected_areas || []).join(", ")} />
      </Section>

      {/* Scope of Work */}
      {s6.areas && (
        <Section title="Scope of Work">
          {s6.areas.map((a, i) => (
            <div key={i} className="border border-zinc-200 rounded p-2 mb-1" data-testid={`ir-scope-${i}`}>
              <div className="flex justify-between"><p className="font-semibold text-[11px]">{a.area}</p>
                <Badge className="text-[9px]">{a.severity}</Badge></div>
              <p className="text-[11px] text-zinc-700">{a.damage_description}</p>
              <p className="text-[11px] text-zinc-600 mt-1"><strong>Scope:</strong> {a.recommended_scope_of_work}</p>
            </div>
          ))}
        </Section>
      )}

      {/* Moisture readings */}
      {(s7.materials_readings && s7.materials_readings.length > 0) && (
        <Section title="Moisture Assessment" icon={<Droplet className="w-3 h-3 text-blue-500" />}>
          <KV label="Water Source" v={s7.water_source} />
          <KV label="Category" v={s7.water_category} />
          <div className="mt-1 space-y-1">
            {s7.materials_readings.map((r, i) => (
              <div key={i} className="text-[11px] text-zinc-700 flex justify-between" data-testid={`ir-mr-${i}`}>
                <span>{r.location} — {r.material}</span>
                <span className="font-mono">{r.moisture_pct}% / {r.dry_standard_pct}% ({r.assessment})</span>
              </div>
            ))}
          </div>
          {s7.thermal_images && s7.thermal_images.length > 0 && (
            <div className="mt-2">
              <p className="text-[10px] uppercase font-bold text-zinc-500 mb-1">Thermal Images</p>
              <div className="grid grid-cols-2 gap-1">
                {s7.thermal_images.map(t => (
                  <div key={t.id} className="rounded overflow-hidden border border-zinc-200" data-testid={`ir-thermal-${t.id}`}>
                    <img src={`${API_BASE}${t.url}`} alt="thermal" className="w-full h-20 object-cover" />
                    <p className="text-[9px] text-zinc-600 p-1 line-clamp-2">{t.ai_analysis}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>
      )}

      {/* Hazards */}
      {(s10.hazards && s10.hazards.length > 0) && (
        <Section title="Hazards Flagged" icon={<ShieldAlert className="w-3 h-3 text-rose-600" />}>
          {s10.hazards.map((h, i) => (
            <Badge key={i} className="bg-rose-100 text-rose-700 mr-1 mb-1 text-[10px]" data-testid={`ir-haz-${i}`}>{h}</Badge>
          ))}
        </Section>
      )}

      {/* Equipment */}
      {s9.placements && s9.placements.length > 0 && (
        <Section title="Equipment Recommendations">
          {s9.placements.map((p, i) => (
            <div key={i} className="text-[11px] text-zinc-700" data-testid={`ir-eq-${i}`}>
              <strong>{p.room}:</strong> {p.dehumidifiers} dehu · {p.air_movers} movers · {p.air_scrubbers} scrubbers
            </div>
          ))}
        </Section>
      )}

      {/* Inventory */}
      {s11.items && s11.items.length > 0 && (
        <Section title="Contents Inventory">
          {s11.items.map((it, i) => (
            <div key={i} className="flex justify-between text-[11px] text-zinc-700" data-testid={`ir-inv-${i}`}>
              <span>{it.name} ({it.location})</span>
              <span><Badge variant="outline" className="text-[9px] mr-1">{it.condition}</Badge>${(it.estimated_value || 0).toFixed(0)}</span>
            </div>
          ))}
        </Section>
      )}

      {/* Photos */}
      {photos.length > 0 && (
        <Section title={`Damage Photos (${photos.length})`} icon={<Camera className="w-3 h-3 text-zinc-500" />}>
          <div className="grid grid-cols-3 gap-1">
            {photos.slice(0, 9).map(p => (
              <img key={p.id} src={`${API_BASE}/api/photos/${p.id}/image`}
                   alt="" className="w-full h-16 object-cover rounded" />
            ))}
          </div>
        </Section>
      )}

      {/* Customer debrief */}
      {s12.debrief_notes && (
        <Section title="Customer Debrief">
          <p className="text-[11px] text-zinc-700 whitespace-pre-wrap">{s12.debrief_notes}</p>
        </Section>
      )}

      {/* Estimate */}
      {s13.total_price && (
        <Section title="Preliminary Estimate" icon={<ClipboardCheck className="w-3 h-3 text-amber-600" />}>
          <p className="text-2xl font-extrabold text-zinc-900" data-testid="ir-estimate-total">
            ${Number(s13.total_price).toLocaleString()}
          </p>
          {s13.final_notes && <p className="text-[11px] text-zinc-600 mt-1 italic">{s13.final_notes}</p>}
          <Button size="sm" variant="outline" className="mt-2 text-[11px] h-8"
                  onClick={() => window.open(`${API_BASE}/api/inspection/${requestId}/step/13/preview-pdf`, "_blank")}
                  data-testid="ir-view-estimate-pdf">
            <FileDown className="w-3 h-3 mr-1" /> View Estimate PDF
          </Button>
          {s14.signed_pdf_url && (
            <Button size="sm" variant="outline" className="mt-2 ml-1 text-[11px] h-8"
                    onClick={() => window.open(`${API_BASE}${s14.signed_pdf_url}`, "_blank")}
                    data-testid="ir-view-signed-paperwork">
              <FileDown className="w-3 h-3 mr-1" /> View Signed {s14.doc_type === "work_auth" ? "Work Auth" : "ToS"}
            </Button>
          )}
        </Section>
      )}

      {/* Action dialog (Request Changes / Reject / Certificate confirm) */}
      {actionDialog && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4"
             data-testid={`mgr-action-dialog-${actionDialog}`}>
          <div className="bg-white rounded-lg p-4 w-full max-w-sm space-y-3">
            <p className="text-sm font-bold text-zinc-900">
              {actionDialog === "changes" ? "Request Changes from Technician"
               : actionDialog === "reject" ? "Reject Estimate"
               : "Issue Certificate of Satisfaction"}
            </p>
            {actionDialog === "certificate" ? (
              <p className="text-xs text-zinc-600">
                This marks the job as fully complete, generates a Certificate of Satisfaction PDF,
                and sends it to the customer's ARM account.
              </p>
            ) : (
              <Textarea value={actionNotes} onChange={e => setActionNotes(e.target.value)}
                        placeholder={actionDialog === "reject" ? "Reason for rejection (required)" : "Notes for the technician (required)"}
                        className="h-24 text-xs" data-testid="mgr-action-notes" />
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setActionDialog(null); setActionNotes(""); }} className="flex-1">Cancel</Button>
              <Button onClick={() => {
                        if (actionDialog === "certificate") {
                          callAction("issue-certificate");
                        } else {
                          if (!actionNotes.trim()) { toast.error("Notes required"); return; }
                          callAction(actionDialog === "changes" ? "request-changes" : "reject", { notes: actionNotes.trim() });
                        }
                      }}
                      disabled={busy}
                      className={`flex-1 text-white ${actionDialog === "reject" ? "bg-rose-600" : actionDialog === "certificate" ? "bg-purple-600" : "bg-zinc-900"}`}
                      data-testid="mgr-action-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="border-t border-zinc-100 pt-2">
      <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-1 flex items-center gap-1">
        {icon}{title}
      </p>
      {children}
    </div>
  );
}
function KV({ label, v }) {
  if (!v) return null;
  return (
    <div className="flex justify-between text-[11px]">
      <span className="text-zinc-500">{label}</span>
      <span className="text-zinc-800 font-medium text-right">{v}</span>
    </div>
  );
}
